// 騎士のすり足の床の砂 — 見本の絵から再現する (2026-09-30)。
//   ユーザー (09-30b): 「すり足はすり足だとわかるようにエフェクトなども適切に追加して仕上げてほしい。プロ仕様に仕上げてほしい。」
//   台帳 production.md「FXの作り方」= 見本の絵 → Claude がコードで再現。台帳 bosses.md「磨きの見定めパック 09-30b の回答」。
//   見本 = そざい/fx/knight-suri-dust/ (4コマ・Codex 票 knight-suri-art-0930)。足 (描かない) が左→右へ床を擦った向き。
//   材料 = public/sprites/fx/knight-suri-dust/frame_01〜04.png (521×96・白・透明度＝明るさ・4コマ共通の切り出し枠)。
//     1 = 擦り始めの小さなもや / 2 = 擦っている最中 (床沿いの細い帯＋足先の前にかき寄せた小さな盛り上がり)
//     3 = 止まった直後 (盛り上がりが低く巻き上がる・帯は途切れる) / 4 = 名残のもや。
//   動き: 滑り (glide) の間は、盛り上がりが前足のつま先と一緒に進み、帯は滑り始めの所からつま先までだけ見える。
//     止まったら 2→3→4 へ移りながら尾から薄れる。後ろ足を引き寄せた時 (draw) は後ろ足の下に1コマ目を小さく。
//   床の層 = 騎士の絵より先に描く (足が砂に被さる)。高さは床から約 16px まで (騎士の脛の半分より低い)。色は騎士の広間の床より一段明るい青灰 (飛び込みの砂と同じ)。
// ブラウザ専用 (Image が無い Vitest では描かない。位置の計算 knightSuriDustSpan はテストできる)。

const BASE = '/sprites/fx/knight-suri-dust/'
/** 材料の床の高さと、つま先の位置 (盛り上がりの頂 x≈420 がつま先の約 6px 前に来る)。4コマ共通。 */
const FLOOR_Y = 87
const FOOT_X = 385
/** 帯の尾 (2コマ目の見える所の左端)。 */
const TAIL_X = 36
/** 縦の大きさ: 盛り上がり (材料で約 54px) が床から約 16px。 */
const SCALE_Y = 0.3
/** 見本の透明度は淡い (盛り上がりでも 0.25 前後)。ゲームの暗い床で読める濃さへ持ち上げる。 */
const ALPHA_GAIN = 1.9
/** 帯の尾を滑り始めの所より少し後ろまで伸ばす (px)。 */
const BEHIND_PX = 14
export const KNIGHT_SURI_SETTLE_MS = 420
export const KNIGHT_SURI_DRAW_MS = 300
const TINT = [150, 156, 176] as const
/** 1コマ目のもやの右端 (材料の px)。 */
const DRAW_SRC_X = 290

/** 滑りの進み方 (0..1 → 0..1)。すり足の動き (体の移動) と砂は必ずこの同じ関数を使う。出だしで押し、終わりで止まる。 */
export function knightSuriGlideEase(u: number): number {
  const t = Math.max(0, Math.min(1, u))
  return 1 - (1 - t) * (1 - t) * (1 - t)
}

let started = false
const frames: (HTMLCanvasElement | null)[] = [null, null, null, null]

function bake(img: HTMLImageElement): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = img.width; c.height = img.height
  const g = c.getContext('2d')!
  g.drawImage(img, 0, 0)
  const d = g.getImageData(0, 0, c.width, c.height)
  const p = d.data
  for (let i = 0; i < p.length; i += 4) {
    if (p[i + 3] <= 0) continue
    const lum = (p[i] * 0.3 + p[i + 1] * 0.59 + p[i + 2] * 0.11) / 255
    const shade = 0.5 + 0.5 * lum   // 鎧 (同じ青灰) より一段暗く、足先と見分ける
    p[i] = Math.min(255, TINT[0] * shade)
    p[i + 1] = Math.min(255, TINT[1] * shade)
    p[i + 2] = Math.min(255, TINT[2] * shade)
    p[i + 3] = Math.min(255, Math.round(p[i + 3] * ALPHA_GAIN))
  }
  g.putImageData(d, 0, 0)
  return c
}

/** 材料の読み込み開始 (main.ts の preload から)。 */
export function preloadKnightSuriDust(): void {
  if (started || typeof Image === 'undefined' || typeof document === 'undefined') return
  started = true
  for (let i = 0; i < 4; i++) {
    const img = new Image()
    img.onload = () => { frames[i] = bake(img) }
    img.src = `${BASE}frame_0${i + 1}.png`
  }
}

/** 1回の滑り。x0→x1 = 前足のつま先 (世界の x)、y = 床、dir = 進む向き (+1 右 / -1 左)、born = 滑り始め (ms)。 */
export interface KnightSuriGlide { kind: 'glide'; x0: number; x1: number; y: number; dir: number; born: number; glideMs: number }
/** 後ろ足の引き寄せ。x = 後ろ足のつま先、born = 引き寄せ始め (ms)。 */
export interface KnightSuriDraw { kind: 'draw'; x: number; y: number; dir: number; born: number }
export type KnightSuriDustEvent = KnightSuriGlide | KnightSuriDraw

/**
 * 滑りの砂の見える範囲 (世界の x)。foot = いまのつま先、tail = 帯の尾、phase = 'glide' | 'settle'。
 * 滑りの前 (age<0) と消えた後は null。
 */
export function knightSuriDustSpan(e: KnightSuriGlide, now: number): { foot: number; tail: number; phase: 'glide' | 'settle'; u: number } | null {
  const age = now - e.born
  if (age < 0 || age > e.glideMs + KNIGHT_SURI_SETTLE_MS) return null
  const tail = e.x0 - e.dir * BEHIND_PX
  if (age <= e.glideMs) {
    const u = e.glideMs > 0 ? age / e.glideMs : 1
    return { foot: e.x0 + (e.x1 - e.x0) * knightSuriGlideEase(u), tail, phase: 'glide', u }
  }
  return { foot: e.x1, tail, phase: 'settle', u: (age - e.glideMs) / KNIGHT_SURI_SETTLE_MS }
}

function drawFrame(ctx: CanvasRenderingContext2D, img: HTMLCanvasElement, footX: number, y: number, dir: number, sx: number, sy: number, alpha: number, srcFootX = FOOT_X): void {
  if (alpha <= 0.003) return
  ctx.globalAlpha = alpha
  ctx.save()
  ctx.translate(footX, y)
  ctx.scale(dir * sx, sy)
  ctx.drawImage(img, -srcFootX, -FLOOR_Y)
  ctx.restore()
}

/** 描く。材料が未読み込みなら false。 */
export function drawKnightSuriDust(ctx: CanvasRenderingContext2D, e: KnightSuriDustEvent, now: number): boolean {
  preloadKnightSuriDust()
  if (frames.some((f) => !f)) return false
  const [f1, f2, f3, f4] = frames as HTMLCanvasElement[]
  ctx.save()
  if (e.kind === 'draw') {
    // 後ろ足を寄せた時: 1コマ目 (小さなもや) を後ろ足の下に。すぐ広がって消える。
    const age = now - e.born
    if (age >= 0 && age <= KNIGHT_SURI_DRAW_MS) {
      const k = age / KNIGHT_SURI_DRAW_MS
      const grow = 0.75 + 0.35 * (1 - (1 - k) * (1 - k))
      // 1コマ目のもやは材料の x≈60〜290 にある。その右端 (寄せた先) を後ろ足のつま先の少し後ろへ。
      drawFrame(ctx, f1, e.x - e.dir * 6, e.y, e.dir, 0.2 * grow, SCALE_Y * 0.8 * grow, 0.9 * (1 - k) * Math.min(1, age / 40), DRAW_SRC_X)
    }
    ctx.restore(); ctx.globalAlpha = 1
    return true
  }
  const span = knightSuriDustSpan(e, now)
  if (!span) { ctx.restore(); return true }
  // 横の大きさ: 帯 (材料の TAIL_X→FOOT_X) が滑った距離＋後ろの伸びに合う。短い滑りでも盛り上がりが潰れないよう下限を持つ。
  const dist = Math.abs(e.x1 - e.x0) + BEHIND_PX
  const sx = Math.max(0.16, dist / (FOOT_X - TAIL_X))
  // 見える範囲 = 尾からつま先の少し前まで (前へ転がる粒を残す)。
  const ahead = span.foot + e.dir * 16
  const x0 = Math.min(span.tail, ahead), x1 = Math.max(span.tail, ahead)
  ctx.beginPath()
  ctx.rect(x0, e.y - 40, x1 - x0, 44)
  ctx.clip()
  if (span.phase === 'glide') {
    // 出だしは1コマ目 (淡い) から2コマ目へ。盛り上がりはつま先と一緒に進む。
    const w = Math.min(1, span.u / 0.35)
    drawFrame(ctx, f1, span.foot, e.y, e.dir, sx, SCALE_Y, 0.8 * (1 - w))
    drawFrame(ctx, f2, span.foot, e.y, e.dir, sx, SCALE_Y * (0.7 + 0.3 * w), 0.55 + 0.45 * w)
  } else {
    // 止まった後: 2→3 (巻き上がる・0〜0.3) → 4 (名残・0.3〜1)。全体は後半で薄れる。
    const u = span.u
    const out = u < 0.45 ? 1 : 1 - (u - 0.45) / 0.55
    const a23 = Math.min(1, u / 0.3)
    const a34 = Math.max(0, Math.min(1, (u - 0.3) / 0.4))
    const drift = e.dir * 5 * (1 - (1 - u) * (1 - u))   // 盛り上がりが少し前へ流れる
    drawFrame(ctx, f2, span.foot, e.y, e.dir, sx, SCALE_Y, out * (1 - a23))
    drawFrame(ctx, f3, span.foot + drift, e.y, e.dir, sx, SCALE_Y, out * a23 * (1 - a34))
    drawFrame(ctx, f4, span.foot + drift, e.y, e.dir, sx, SCALE_Y, out * a34 * 1.4)
  }
  ctx.restore()
  ctx.globalAlpha = 1
  return true
}

/** すり足の砂の置き場。すり足の動き (プロキシ) が glide / draw を足し、render が毎フレーム draw する。 */
export class KnightSuriDust {
  events: KnightSuriDustEvent[] = []
  glide(x0: number, x1: number, y: number, dir: number, born: number, glideMs: number): void {
    this.events.push({ kind: 'glide', x0, x1, y, dir: dir >= 0 ? 1 : -1, born, glideMs })
  }
  draw(x: number, y: number, dir: number, born: number): void {
    this.events.push({ kind: 'draw', x, y, dir: dir >= 0 ? 1 : -1, born })
  }
  clear(): void { this.events = [] }
  render(ctx: CanvasRenderingContext2D, now: number): void {
    this.events = this.events.filter((e) => {
      const age = now - e.born
      if (age < -1000) return false   // 時計が戻った (やり直し・部屋替え)
      return e.kind === 'glide' ? age <= e.glideMs + KNIGHT_SURI_SETTLE_MS : age <= KNIGHT_SURI_DRAW_MS
    })
    for (const e of this.events) drawKnightSuriDust(ctx, e, now)
  }
}

export const knightSuriDust = new KnightSuriDust()
